import type { Placement, UseFloatingReturn, UseInteractionsReturn } from "@floating-ui/react";
import {
	FloatingDelayGroup,
	FloatingPortal,
	autoUpdate,
	flip,
	offset,
	shift,
	useDelayGroup,
	useDismiss,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
	useMergeRefs,
	useRole,
	useTransitionStatus,
} from "@floating-ui/react";
import { cva } from "class-variance-authority";
import * as React from "react";
import { classNames } from "../../utils/classNames";

const tooltipStyles = cva("z-50 text-xs transition-[opacity] duration-200 ease-in-out");

const containerStyles = cva("rounded-md", {
	variants: {
		variant: {
			text: "max-w-[clamp(25ch,70vw,50ch)] whitespace-normal break-words bg-neutral px-2 py-1 text-center text-on-neutral",
			info: "border border-neutral bg-white p-4 [&>:where(svg)]:overflow-hidden [&>:where(svg)]:rounded [&_:where(img)]:rounded",
		},
	},
});

interface TooltipOptions {
	initialOpen?: boolean;
	placement?: Placement;
	open?: boolean;
	offset?: Parameters<typeof offset>[0];
	onOpenChange?: (open: boolean) => void;
	delay?: number | Partial<{ open: number; close: number }>;
}

const TooltipGroupPresenceContext = React.createContext(false);

export function TooltipGroup({
	children,
	delay = { open: 300, close: 100 },
	timeoutMs = 300,
}: {
	children: React.ReactNode;
	delay?: TooltipOptions["delay"];
	timeoutMs?: number;
}) {
	return (
		<TooltipGroupPresenceContext.Provider value={true}>
			<FloatingDelayGroup delay={delay} timeoutMs={timeoutMs}>
				{children}
			</FloatingDelayGroup>
		</TooltipGroupPresenceContext.Provider>
	);
}

// Spelled out rather than inferred: the inferred type reaches into @floating-ui/react-dom, which
// TypeScript cannot name portably from here.
export type TooltipState = UseFloatingReturn &
	UseInteractionsReturn & {
		open: boolean;
		setOpen: (open: boolean) => void;
	};

export function useTooltip({
	initialOpen = false,
	placement = "top",
	open: controlledOpen,
	offset: offsetValue = 5,
	onOpenChange: setControlledOpen,
	delay,
}: TooltipOptions = {}): TooltipState {
	const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);

	const open = controlledOpen ?? uncontrolledOpen;
	const setOpen = setControlledOpen ?? setUncontrolledOpen;

	const data = useFloating({
		placement,
		open,
		onOpenChange: setOpen,
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(offsetValue),
			flip({
				crossAxis: placement.includes("-"),
				fallbackAxisSideDirection: "start",
				padding: 5,
			}),
			shift({ padding: 5 }),
		],
	});

	const context = data.context;

	const isInGroup = React.useContext(TooltipGroupPresenceContext);
	const { delay: groupDelay } = useDelayGroup(context);

	const effectiveDelay = isInGroup ? groupDelay : delay;

	const hover = useHover(context, {
		move: false,
		enabled: controlledOpen == null,
		delay: effectiveDelay,
	});
	const focus = useFocus(context, {
		enabled: controlledOpen == null,
	});
	const dismiss = useDismiss(context, { referencePress: controlledOpen == null });
	const role = useRole(context, { role: "tooltip" });

	const interactions = useInteractions([hover, focus, dismiss, role]);

	return React.useMemo(
		() => ({
			open,
			setOpen,
			...interactions,
			...data,
		}),
		[open, setOpen, interactions, data],
	);
}

type ContextType = TooltipState | null;

const TooltipContext = React.createContext<ContextType>(null);

function getElementRef(element: React.ReactElement): React.Ref<unknown> | undefined {
	// React 19 treats `ref` as a regular prop, while older versions expose it on the element.
	// This mirrors the compatibility approach used by headless UI libraries to avoid dev warnings.
	let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get;
	let mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
	if (mayWarn) {
		return (element as { ref?: React.Ref<unknown> }).ref;
	}

	getter = Object.getOwnPropertyDescriptor(element, "ref")?.get;
	mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
	if (mayWarn) {
		return (element.props as { ref?: React.Ref<unknown> }).ref;
	}

	return (element.props as { ref?: React.Ref<unknown> }).ref ?? (element as { ref?: React.Ref<unknown> }).ref;
}

export const useTooltipContext = (): TooltipState => {
	const context = React.useContext(TooltipContext);

	if (context == null) {
		throw new Error("Tooltip components must be wrapped in <Tooltip />");
	}

	return context;
};

export function Tooltip({ children, ...options }: { children: React.ReactNode } & TooltipOptions) {
	// This can accept any props as options, e.g. `placement`,
	// or other positioning options.
	const tooltip = useTooltip(options);
	return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

export const TooltipTrigger = React.forwardRef<HTMLElement, React.HTMLProps<HTMLElement>>(function TooltipTrigger(
	{ children, ...props },
	propRef,
) {
	const context = useTooltipContext();
	const childrenRef = React.isValidElement(children) ? getElementRef(children) : undefined;
	const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

	// `asChild` allows the user to pass any element as the anchor
	if (React.isValidElement(children)) {
		const child = children as React.ReactElement<Record<string, unknown>>;

		return React.cloneElement(child, {
			...context.getReferenceProps({
				...props,
				...child.props,
				ref,
			}),
			"data-state": context.open ? "open" : "closed",
		});
	}

	return null;
});

export const TooltipContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLProps<HTMLDivElement> & { variant?: "text" | "info" }
>(function TooltipContent({ style, variant = "text", ...props }, propRef) {
	const context = useTooltipContext();
	const { isMounted, status } = useTransitionStatus(context.context, {
		duration: {
			open: 200,
			close: 100,
		},
	});
	const ref = useMergeRefs([context.refs.setFloating, propRef]);

	if (!context.open) {
		return null;
	}

	if (!isMounted) {
		return null;
	}

	return (
		<FloatingPortal>
			<div
				ref={ref}
				style={{
					...context.floatingStyles,
					...style,
				}}
				{...context.getFloatingProps(props)}
				className={classNames(
					tooltipStyles(),
					props.className,
					"pointer-events-none",
					status === "open" ? "opacity-100" : "opacity-0",
				)}
			>
				<div className={containerStyles({ variant })}>{props.children}</div>
			</div>
		</FloatingPortal>
	);
});
